import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Spin } from 'antd';
import { loadAMap } from '@/utils/amap';
import { coordinatePair, gcj02ToWgs84, wgs84ToGcj02 } from '@/utils/coordinates';

const EMPTY_POSTS = [];
const MapCanvas = ({ longitude, latitude, currentPosition, posts = EMPTY_POSTS, heatmap = EMPTY_POSTS, onPick, disabled }) => {
  const container = useRef(null); const mapRef = useRef(null); const onPickRef = useRef(onPick);
  const disabledRef = useRef(disabled);
  const cleanupOverlays = useRef(() => {});
  const centerRef = useRef(null);
  const [ready, setReady] = useState(0); const [attempt, setAttempt] = useState(0);
  const [error, setError] = useState('');
  const [heatmapError, setHeatmapError] = useState('');
  onPickRef.current = onPick; disabledRef.current = disabled;
  centerRef.current = coordinatePair(longitude, latitude) || coordinatePair(currentPosition?.[0], currentPosition?.[1]);

  useEffect(() => {
    let active = true; let map;
    setError(''); setReady(0);
    loadAMap().then(AMap => {
      if (!active) return;
      map = new AMap.Map(container.current, { zoom: 13, viewMode: '2D', center: centerRef.current ? wgs84ToGcj02(centerRef.current) : [116.397428, 39.90923] });
      mapRef.current = map;
      map.on('click', event => {
        if (!disabledRef.current) onPickRef.current?.(gcj02ToWgs84([event.lnglat.getLng(), event.lnglat.getLat()]));
      });
      setReady(value => value + 1);
    }).catch(err => { if (active) setError(err.message || '地图暂时无法加载'); });
    return () => { active = false; cleanupOverlays.current(); map?.destroy(); mapRef.current = null; };
  }, [attempt]);

  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map) return undefined;
    const AMap = window.AMap;
    const overlays = []; const windows = [];
    const point = coordinatePair(longitude, latitude);
    const center = point || coordinatePair(currentPosition?.[0], currentPosition?.[1]);
    if (center) {
      const marker = new AMap.Marker({ position: wgs84ToGcj02(center), title: point ? '所选位置' : '当前位置' });
      overlays.push(marker); map.setCenter(wgs84ToGcj02(center));
    }
    posts.forEach(post => {
      const coordinates = coordinatePair(post.longitude, post.latitude);
      if (!coordinates || !['approximate', 'exact'].includes(post.location_visibility)) return;
      const marker = new AMap.Marker({ position: wgs84ToGcj02(coordinates), title: post.title });
      marker.on('click', () => {
        const content = document.createElement('div'); content.className = 'location-picker-popup';
        const link = document.createElement('a'); link.textContent = post.title; link.href = `/posts/${post.id}`;
        const detail = document.createElement('p');
        detail.textContent = `${post.location_visibility === 'approximate' ? '大致位置' : '公开位置'}${post.distance_km != null ? ` · 距离约 ${post.distance_km} 公里` : ''}`;
        content.append(link, detail);
        windows.forEach(info => info.close());
        const info = new AMap.InfoWindow({ content, offset: new AMap.Pixel(0, -30) });
        windows.push(info); info.open(map, marker.getPosition());
      });
      overlays.push(marker);
    });
    map.add(overlays);
    let removed = false;
    const cleanup = () => {
      if (removed) return;
      removed = true; windows.forEach(info => info.close()); map.remove(overlays);
    };
    cleanupOverlays.current = cleanup;
    return cleanup;
  }, [ready, longitude, latitude, currentPosition, posts]);

  useEffect(() => {
    const map = mapRef.current;
    let active = true; let layer; let timer;
    setHeatmapError('');
    if (!ready || !map || !heatmap.length) return undefined;
    const AMap = window.AMap;
    timer = setTimeout(() => { if (active) setHeatmapError('热力图图层加载超时，请重新开启热力图'); }, 15000);
    AMap.plugin('AMap.HeatMap', () => {
      if (!active) return;
      clearTimeout(timer);
      setHeatmapError('');
      try {
        layer = new AMap.HeatMap(map, { radius: 30, opacity: [0, 0.65], gradient: { 0.2: '#d9d7e6', 0.5: '#9b95b4', 0.8: '#615a80', 1: '#272636' } });
        layer.setDataSet({ data: heatmap.map(cell => { const [lng, lat] = wgs84ToGcj02([cell.longitude, cell.latitude]); return { lng, lat, count: cell.count }; }), max: Math.max(...heatmap.map(cell => cell.count)) });
      } catch { setHeatmapError('热力图图层暂时无法显示，请重新开启热力图'); }
    });
    return () => { active = false; clearTimeout(timer); layer?.setMap(null); };
  }, [ready, heatmap]);

  return <div className="location-picker-map-shell">
    {heatmapError && <Alert type="warning" title={heatmapError} />}
    <div ref={container} className="location-picker-map" aria-label="位置地图" />
    {!ready && <div className="location-picker-map-state">
      {error ? <Alert type="warning" showIcon title={error} action={<Button size="small" onClick={() => setAttempt(value => value + 1)}>重试</Button>} /> : <Spin tip="地图加载中…"><div /></Spin>}
    </div>}
  </div>;
};
export default MapCanvas;
